import { supabase } from "@/lib/supabase";
import { describeFunctionsError } from "@/lib/supabaseFunctionsError";
import type {
  ActivityQuery,
  AgentActivityEntry,
  AgentConnection,
  IAgentService,
  OwnedAgent,
} from "@/services/interfaces/IAgentService";

/** Every action is one POST to agent-admin, which checks ownership itself. */
async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("agent-admin", { body });
  if (error) throw new Error(await describeFunctionsError(error));
  return data as T;
}

export class SupabaseAgentService implements IAgentService {
  async listMine(): Promise<OwnedAgent[]> {
    return (await call<{ agents: OwnedAgent[] }>({ action: "list" })).agents;
  }

  async create(name: string, label: string): Promise<{ agent: OwnedAgent; key: string }> {
    return await call({ action: "create", name, label });
  }

  async addConnection(agentId: string, label: string): Promise<{ connection: AgentConnection; key: string }> {
    return await call({ action: "add_connection", agentId, label });
  }

  async revokeConnection(agentId: string, keyId: string): Promise<void> {
    await call({ action: "revoke_connection", agentId, keyId });
  }

  async pause(agentId: string): Promise<void> {
    await call({ action: "pause", agentId });
  }

  async resume(agentId: string): Promise<void> {
    await call({ action: "resume", agentId });
  }

  async revoke(agentId: string): Promise<void> {
    await call({ action: "revoke", agentId });
  }

  async listActivity(agentId: string, opts: ActivityQuery): Promise<AgentActivityEntry[]> {
    let q = supabase.from("agent_activity").select("*").eq("agent_id", agentId);
    if (opts.keyId) q = q.eq("key_id", opts.keyId);
    if (opts.chainId !== undefined) q = q.or(`chain_id.eq.${opts.chainId},chain_id.is.null`);
    if (opts.before) {
      const c = opts.before.createdAt;
      q = q.or(`created_at.lt.${c},and(created_at.eq.${c},id.lt.${opts.before.id})`);
    }
    const { data, error } = await q
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(opts.limit ?? 50);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      id: r.id as number,
      agentId: r.agent_id as string,
      keyId: (r.key_id as string | null) ?? null,
      action: r.action as string,
      entityTable: (r.entity_table as string | null) ?? null,
      entityId: (r.entity_id as string | null) ?? null,
      chainId: (r.chain_id as number | null) ?? null,
      detail: (r.detail as Record<string, unknown> | null) ?? {},
      createdAt: r.created_at as string,
    }));
  }
}
